import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { 
  BookOpen, 
  Home, 
  Utensils, 
  Users, 
  Heart, 
  Briefcase,
  GraduationCap,
  Calendar,
  ChevronRight
} from 'lucide-react';

export default function StudentExperience() {
  const { t } = useTranslation();

  const supportServices = [
    { icon: <BookOpen className="w-6 h-6" />, title: 'Academic Advising', desc: 'Get guidance on course selection, academic planning, and career pathways.', href: '#' },
    { icon: <Briefcase className="w-6 h-6" />, title: 'Career Services', desc: 'Access job boards, resume workshops, interview preparation, and networking events.', href: '#' },
    { icon: <Heart className="w-6 h-6" />, title: 'Counseling Services', desc: 'Confidential mental health support and wellness resources for all students.', href: '#' },
    { icon: <GraduationCap className="w-6 h-6" />, title: 'Accessibility Services', desc: 'Accommodations and support for students with disabilities.', href: '#' }
  ];

  const campusLife = [
    { icon: <Home className="w-6 h-6" />, title: 'Housing', desc: 'On-campus and off-campus housing options and resources.', href: '#' },
    { icon: <Utensils className="w-6 h-6" />, title: 'Dining', desc: 'Campus dining facilities and meal plan options.', href: '#' },
    { icon: <Users className="w-6 h-6" />, title: 'Student Association', desc: 'Get involved with student government and representation.', href: '#' },
    { icon: <Calendar className="w-6 h-6" />, title: 'Clubs & Activities', desc: 'Join student clubs, organizations, and campus events.', href: '#' }
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('nav_student_experience', 'Student Experience')}
        description="Your journey at ESTAD goes beyond the classroom. Discover support services, campus life, and opportunities to thrive."
        breadcrumbs={[{ label: 'Student Experience', href: '/student-experience' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="admissions" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* Hero Banner Image */}
          <div className="w-full h-64 md:h-96 rounded-sm overflow-hidden shadow-xl border-b-8 border-gold">
            <img src="/images/campus/students-01.jpg" alt="Student Life" className="w-full h-full object-cover" />
          </div>

          {/* Support Services Grid */}
          <section id="support" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic leading-none">
              <Heart className="w-8 h-8 text-gold" /> Student Support
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {supportServices.map((service, idx) => (
                <div key={idx} className="group bg-gray-50 p-8 border-l-8 border-navy hover:bg-white hover:shadow-2xl hover:border-gold transition-all duration-300 rounded-sm">
                   <div className="flex justify-between items-start mb-6">
                      <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center text-navy group-hover:scale-110 transition-transform">
                        {service.icon}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gold transition-colors" />
                   </div>
                   <h3 className="text-xl font-black uppercase tracking-tight mb-2 leading-none text-navy">
                    {service.title}
                  </h3>
                  <p className="text-gray-500 text-sm font-medium leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Campus Life Highlight */}
          <section id="life" className="bg-navy p-8 md:p-16 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-1000"></div>
            <div className="relative z-10 space-y-12">
               <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic italic leading-tight">
                Live, Learn, and Lead
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {campusLife.map((item, idx) => (
                  <div key={idx} className="space-y-4 text-center">
                    <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center mx-auto border border-gold/30">
                      <div className="text-gold">{item.icon}</div>
                    </div>
                    <h4 className="font-black text-sm uppercase tracking-widest text-gold">{item.title}</h4>
                    <p className="text-white/60 text-[10px] font-medium leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Quick Contact Banner */}
          <section id="help" className="border-2 border-navy/5 p-10 md:p-16 rounded-sm bg-offWhite flex flex-col md:flex-row gap-12 items-center shadow-inner relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-2 h-full bg-gold"></div>
            <div className="w-32 h-32 rounded-full bg-navy flex items-center justify-center flex-shrink-0 shadow-2xl border-4 border-gold/30 group-hover:scale-105 transition-transform">
               <Users className="w-16 h-16 text-gold" />
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl font-black text-navy uppercase tracking-tighter italic leading-none">Need immediate assistance?</h3>
              <p className="text-gray-600 text-lg leading-relaxed font-medium max-w-2xl">
                Our Student Services team is here to help you navigate campus life and academic challenges.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                 <Link to="/contact" className="bg-navy text-white px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-gold hover:text-navy transition-all active:scale-95">Contact Student Services</Link>
                 <Link to="/admissions" className="border-2 border-navy/20 text-navy px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-navy hover:text-white transition-all active:scale-95">Admissions Portal</Link>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
